import React from 'react';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import PropTypes from 'prop-types';

const libraries = ['places'];

const ServiceLocationStep = ({ formData, handleInputChange, errors, updateFormData }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const [autocomplete, setAutocomplete] = React.useState(null);

  const onLoad = (autoC) => setAutocomplete(autoC);

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      let newFormData = {
        ...formData,
        address: place.formatted_address,
        addressName: place.name,
        streetAddress: '',
        city: '',
        postalCode: '',
        latitude: place.geometry?.location.lat() || '',
        longitude: place.geometry?.location.lng() || ''
      };
      
      for (const component of place.address_components) {
        const componentType = component.types[0];
        switch (componentType) {
          case 'street_number':
            newFormData.streetAddress = component.long_name + ' ';
            break;
          case 'route':
            newFormData.streetAddress += component.long_name;
            break;
          case 'locality':
            newFormData.city = component.long_name;
            break;
          case 'postal_code':
            newFormData.postalCode = component.long_name;
            break;
          default:
            break;
        }
      }
      updateFormData(newFormData);
    }
  };

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading maps</div>;

  return (
    <div>
      <div className="mb-4">
        <label htmlFor="address" className="block text-gray-700 text-sm font-bold mb-2">
          Address (Australia only)
        </label>
        <Autocomplete
          onLoad={onLoad}
          onPlaceChanged={onPlaceChanged}
          restrictions={{ country: "au" }}
          fields={["address_components", "geometry", "name", "formatted_address"]}
        >
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address || ''}
            onChange={handleInputChange}
            placeholder="Enter your address in Australia"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            required
          />
        </Autocomplete>
        {errors.address && <p className="mt-1 text-sm text-red-500">{errors.address}</p>}
      </div>
      <div className="mb-4">
        <label htmlFor="streetAddress" className="block text-gray-700 text-sm font-bold mb-2">
          Street Address
        </label>
        <input
          type="text"
          id="streetAddress"
          name="streetAddress"
          value={formData.streetAddress || ''}
          onChange={handleInputChange}
          placeholder="Street Address"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required
        />
        {errors.streetAddress && <p className="mt-1 text-sm text-red-500">{errors.streetAddress}</p>}
      </div>
      <div className="mb-4">
        <label htmlFor="city" className="block text-gray-700 text-sm font-bold mb-2">
          City
        </label>
        <input
          type="text"
          id="city"
          name="city"
          value={formData.city || ''}
          onChange={handleInputChange}
          placeholder="City"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required
        />
        {errors.city && <p className="mt-1 text-sm text-red-500">{errors.city}</p>}
      </div>
      <div className="mb-4">
        <label htmlFor="postalCode" className="block text-gray-700 text-sm font-bold mb-2">
          Postal Code
        </label>
        <input
          type="text"
          id="postalCode"
          name="postalCode"
          value={formData.postalCode || ''}
          onChange={handleInputChange}
          placeholder="Postal Code"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required
        />
        {errors.postalCode && <p className="mt-1 text-sm text-red-500">{errors.postalCode}</p>}
      </div>
    </div>
  );
};

ServiceLocationStep.propTypes = {
  formData: PropTypes.object.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
};

export default ServiceLocationStep;